import React from 'react';
import { getIntl } from '../../../../../utils/intl';
import Stack, { contentStackSpacing } from '../../../../v2BaseComponents/Stack';
import useOneUpHealthEndpoint from '../../../../../hooks/api/useOneUpEndpoint';
import { Image, Platform, View } from 'react-native';
import urls from '../../../../../utils/urls';
import { useAvatar } from '../../../../../hooks/useAvatar';
import SkeletonLoadingContainer from '../../../../v2BaseComponents/SkeletonLoadingContainer';
import { Loader } from '../../../../v2BaseComponents/Loader';
import Text from '../../../../v2BaseComponents/Text';
import { PromptActions } from '../../../../v2BaseComponents/Prompt';
import MainContentContainer from '../../../../v2BaseComponents/MainContentContainer';

const intl = getIntl('screens.profiles.addPortalScreen.portalConnectionConfirmationContent', {
    mainContent: 'Are you sure you want to connect to "{name}"?',
    connectingTo: 'Connecting to "{name}"...',
});

export type PortalConnectionConfirmationContentOwnProps = {
    endpointId: number;
    onConnect: () => void;
    onCancel: () => void;
    isConnecting: boolean;
};

export type PortalConnectionConfirmationContentProps = PortalConnectionConfirmationContentOwnProps;

const imageSize = 84;

const loadingHeight = 252;

const PortalConnectionConfirmationContent: React.FC<PortalConnectionConfirmationContentProps> = (props) => {
    const { endpoint, isLoading } = useOneUpHealthEndpoint({
        endpointId: props.endpointId,
    });
    const image = useAvatar({
        avatarAPIUrl: endpoint?.logo
            ? Platform.select({
                  // On the web, we hit a CORS issue, so we have to go through a proxy
                  web: urls.api.ehrIntegrations.oneUpHealth.endpoints.image(endpoint.logo),
                  default: endpoint.logo,
              })
            : null,
    });

    const renderLogo = () => (
        <Stack horizontal fullWidth justify="center">
            <SkeletonLoadingContainer isLoading={isLoading || image.isLoadingAvatar}>
                <View style={{ width: imageSize, height: imageSize }}>
                    {image.isLoadingAvatar ? null : (
                        <Image
                            style={{
                                width: imageSize,
                                height: imageSize,
                            }}
                            resizeMode={'contain'}
                            source={{
                                uri: image?.avatar?.base64,
                            }}
                            alt={'Health system logo'}
                        />
                    )}
                </View>
            </SkeletonLoadingContainer>
        </Stack>
    );

    const renderContent = () => {
        if (isLoading) {
            return (
                <View style={{ height: loadingHeight, width: '100%', justifyContent: 'center', alignItems: 'center' }}>
                    <Loader />
                </View>
            );
        } else if (props.isConnecting) {
            return (
                <Stack
                    vertical
                    fullWidth
                    space={contentStackSpacing}
                    style={{
                        height: loadingHeight,
                    }}
                    justify="center"
                    align="center"
                >
                    <Loader />
                    <Text variant="caption-1" alignText="center">
                        {intl('connectingTo', { name: endpoint.name })}
                    </Text>
                </Stack>
            );
        } else {
            return (
                <Stack vertical fullWidth space={contentStackSpacing}>
                    {renderLogo()}
                    <Text variant="body-1" alignText="center">
                        {intl('mainContent', { name: endpoint.name })}
                    </Text>
                    <PromptActions
                        actions={[
                            {
                                title: 'Connect',
                                onPress: props.onConnect,
                                variant: 'primary',
                                icon: 'HorizontalArrows2',
                                testID: 'connect-to-portal-confirm',
                            },
                            {
                                title: 'Cancel',
                                onPress: props.onCancel,
                                variant: 'secondary',
                                icon: 'XCircle',
                                testID: 'connect-to-portal-cancel',
                            },
                        ]}
                    />
                </Stack>
            );
        }
    };

    return <MainContentContainer>{renderContent()}</MainContentContainer>;
};

export default React.memo(PortalConnectionConfirmationContent);
